import React, { PropsWithChildren, ReactElement, useEffect, useState, useMemo } from 'react';
import { useTable, useSortBy, useExpanded, usePagination, useRowSelect, TableOptions, Column } from 'react-table';
import { FaPlus, FaUpload, FaExclamationTriangle } from 'react-icons/fa';
import styled from 'styled-components';
import { VscActivateBreakpoints } from 'react-icons/vsc';
import Button from 'components/Button';
import { usePermissionsContext } from 'store/permissions';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import { BiSelectMultiple } from 'react-icons/bi';
import { useSelectableTableContext } from 'store/selectable-table';
import GlobalFilter from './GlobalFilter';
import TableFilters from './TableFilters';
import TableComponent from './TableComponent';
import Pagination from './Pagination';
import { ISelectActionButton, ITableFilter } from './types';
import { SetSortParamType } from './FetchTable';

export interface ITableProps<T extends Record<string, unknown>> extends TableOptions<T> {
  pagination?: boolean;
  offset?: number;
  limit?: number;
  search?: string | null;
  totalCount?: number;
  setOffset?: (offset: number) => void;
  setLimit?: (limit: number) => void;
  setSearch?: (search: string | null) => void;
  hasPermission?: string[];
  hasImportPermission?: boolean;
  onImport?: () => void;
  onAdd?: () => void;
  onMultipleActivation?: () => void;
  addBtnLabel?: string;
  addBtnTestId?: string;
  multipleActivateBtnLabel?: string;
  filters?: ITableFilter[];
  hideFilterBar?: boolean;
  className?: string;
  error?: string | null;
  entityName?: string;
  skipUpdateColumns?: boolean;
  skipResetExpanded?: boolean;
  isExpandable?: boolean;
  isSelectable?: boolean;
  showSelectAll?: boolean;
  useCustomSelectBtn?: boolean;
  customSelectableRowIds?: number[];
  toggleSelectBtnLabel?: string;
  selectActionButtons?: ISelectActionButton[];
  docTab?: string;
  expandAllRows?: boolean;
  hideSearch?: boolean;
  disablePagination?: boolean;
  isLeavesMobile?: boolean;
  setSortParam?: SetSortParamType;
  setSortParamRef?: SetSortParamType;
  disableMultiSort?: boolean;
  useBackendSorting?: boolean;
  checkWithTooltip?: number[];
  showID?: boolean;
  showFilterWarning?: boolean;
  unprocessedLeaves?: {
    count: number;
    min_date: string | null;
    max_date: string | null;
  } | null;
}

function ReactTableWrapper<T extends Record<string, unknown>>({
  loading,
  error,
  data,
  columns,
  hasPermission,
  hasImportPermission,
  onImport,
  onAdd,
  onMultipleActivation,
  addBtnLabel,
  addBtnTestId,
  multipleActivateBtnLabel,
  pagination = false,
  offset = 0,
  limit = 50,
  search = null,
  totalCount = 50,
  setOffset = () => {},
  setLimit = () => {},
  setSearch = () => {},
  filters,
  hideFilterBar,
  className,
  entityName,
  skipUpdateColumns,
  skipResetExpanded = false,
  isExpandable,
  isSelectable,
  showSelectAll,
  useCustomSelectBtn,
  customSelectableRowIds,
  toggleSelectBtnLabel,
  selectActionButtons,
  docTab,
  expandAllRows,
  hideSearch = false,
  disablePagination = false,
  isLeavesMobile = false,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  setSortParam,
  setSortParamRef,
  disableMultiSort = false,
  useBackendSorting = false,
  checkWithTooltip,
  showID,
  showFilterWarning = false,
  unprocessedLeaves,
}: PropsWithChildren<ITableProps<T>>): ReactElement {
  const [tableData, setTableData] = useState(data);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = useMemo(() => {
    if (!filters) return false;
    return filters.some(filter => {
      const { value } = filter;
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
  }, [filters]);

  const {
    showSelectColumn,
    setShowSelectColumn,
    selectColPosition,
    setSelectedRowDataIds,
    selectedRowDataIds,
    selectAllRows,
  } = useSelectableTableContext();

  useEffect(() => {
    // This will run whenever `selectedFlatRows` changes.
  }, [selectedRowDataIds]);

  useEffect(() => {
    if (!showSelectColumn) {
      setSelectedRowDataIds([], false);
      toggleAllRowsSelected(false);
    }
  }, [showSelectColumn]);

  const tableColumns = React.useMemo(
    () => {
      const selectColumn: Column<T> = {
        id: 'selection',
        // The header can use the table's getToggleAllRowsSelectedProps method
        // to render a checkbox
        Header: ({ getToggleAllRowsSelectedProps }) => {
          const { onChange: toggleAllRowsSelectedOnChange, ...props } = getToggleAllRowsSelectedProps();
          /* select all toggle */
          return (
            <div className="d-flex align-items-center justify-content-center">
              {t('form.selectAll')}
              <Form.Check
                className="ml-2"
                custom
                id="checkbox-select-all"
                type="checkbox"
                {...getToggleAllRowsSelectedProps()}
                onChange={e => {
                  if (toggleAllRowsSelectedOnChange && customSelectableRowIds?.length) {
                    if (!e.target.checked) {
                      toggleAllRowsSelectedOnChange(e);
                      selectAllRows(customSelectableRowIds, false);
                    } else {
                      toggleAllRowsSelectedOnChange(e);
                      selectAllRows(customSelectableRowIds, true);
                    }
                  }
                }}
              />
            </div>
          );
        },
        // The cell can use the individual row's getToggleRowSelectedProps method
        // to render a checkbox
        Cell: ({ row }: { row: any }) => {
          const { checked, onChange, ...props } = row.getToggleRowSelectedProps();
          const isTooltipRow =
            (checkWithTooltip?.includes(row.original.id) && row.original.status === 'approved') ?? false;

          if (customSelectableRowIds?.length) {
            if (customSelectableRowIds?.includes(row.original.id)) {
              return (
                <div
                  className="text-center"
                  onClick={e => e.stopPropagation()}
                >
                  <Form.Check
                    custom
                    type="checkbox"
                    id={`checkbox-${row.original.id}`}
                    {...row.getToggleRowSelectedProps()}
                    onChange={e => {
                      onChange(e);
                      setSelectedRowDataIds([row.original.id], e.target.checked);
                    }}
                  />
                </div>
              );
            }

            return (
              <div
                className="text-center"
                onClick={e => e.stopPropagation()}
              >
                {isTooltipRow ? (
                  <TooltipWrapper
                    id={`tooltip-${row.original.id}`}
                    overlay={t('leaves.notEnoughDaysMsg')}
                    wrapperClass="d-block"
                  >
                    <Form.Check
                      custom
                      type="checkbox"
                      id={`checkbox-${row.original.id}`}
                      disabled
                    />
                  </TooltipWrapper>
                ) : (
                  <Form.Check
                    custom
                    type="checkbox"
                    id={`checkbox-${row.original.id}`}
                    disabled
                  />
                )}
              </div>
            );
          }

          if (showSelectAll) {
            return (
              <div
                className="text-center"
                onClick={e => e.stopPropagation()}
              >
                <Form.Check
                  custom
                  type="checkbox"
                  id={`checkbox-${row.original.id}`}
                  value={checked}
                  onChange={e => {
                    onChange(e);
                  }}
                  {...props}
                />
              </div>
            );
          }

          return (
            <div className="text-center">
              {isTooltipRow ? (
                <TooltipWrapper
                  id={`tooltip-${row.original.id}`}
                  overlay={t('leaves.notEnoughDaysMsg')}
                  wrapperClass="d-block"
                >
                  <Form.Check
                    custom
                    type="checkbox"
                    id={`checkbox-${row.original.id}`}
                    disabled
                  />
                </TooltipWrapper>
              ) : (
                <Form.Check
                  custom
                  type="checkbox"
                  id={`checkbox-${row.original.id}`}
                  disabled
                />
              )}
            </div>
          );
        },
        width: '4%',
      };
      if (isSelectable) {
        let cols: Column<T>[] = [selectColumn, ...columns];

        if (selectColPosition === 'end') cols = [...columns, selectColumn];

        return cols;
      }

      return columns;
    },
    !skipUpdateColumns ? [columns, showSelectColumn] : []
  );
  const { t } = useTranslation();
  const { checkPermission } = usePermissionsContext();

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const updateCellData = (rowIndex: number, columnId: number, value: any) => {
    // Тurn on the flag to not reset the page
    setSkipPageReset(true);
    setTableData(old =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  useEffect(() => {
    setSkipPageReset(false);
  }, [tableData]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    toggleAllRowsExpanded,
    prepareRow,
    visibleColumns,
    pageOptions,
    page: rows,
    setHiddenColumns,
    toggleAllRowsSelected,
    selectedFlatRows,
  } = useTable<T>(
    {
      disableMultiSort,
      columns: tableColumns,
      data: tableData,
      autoResetPage: !skipPageReset,
      autoResetExpanded: !skipResetExpanded,
      useControlledState: state => {
        return React.useMemo(
          () => ({
            ...state,
            pageIndex: Math.floor(offset / limit),
          }),
          [state, offset, limit]
        );
      },
      initialState: {
        hiddenColumns: !showID ? ['id', 'selection'] : ['selection'],
        pageIndex: Math.floor(offset / limit),
      },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / limit),
      updateCellData,
      manualSortBy: useBackendSorting,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    const hiddenCols: string[] = [];

    if (!showID) hiddenCols.push('id');
    if (!showSelectColumn) hiddenCols.push('selection');

    setHiddenColumns(hiddenCols);
  }, [showID, showSelectColumn]);

  const getSelectedDataIds = (): number[] => selectedFlatRows.map(row => row.original.id as number);

  const renderAddButton = () => {
    // If no permissions provided init as `true`
    let grantedPermission = !hasPermission;

    if (hasPermission)
      // We're using OR logical operator here to check
      // if we have any one of the lister permissions
      // TODO: See if there will be a need for defining the logical operator in other cases
      // For example, using AND for having all permissions listed
      grantedPermission = hasPermission.reduce((res: boolean, permission: string) => {
        return res || checkPermission(permission);
      }, false);

    return (
      <TooltipWrapper
        id="tooltip-add-btn"
        overlay={t('common.noPermission')}
        show={grantedPermission ? false : undefined}
        wrapperClass="d-block col-xs-12 col-md-auto mb-0"
      >
        <Button
          className="btn-block text-truncate"
          onClick={onAdd}
          disabled={!grantedPermission}
          title={addBtnLabel}
          testId={addBtnTestId}
        >
          <FaPlus className="mr-2" />
          {addBtnLabel}
        </Button>
        {multipleActivateBtnLabel && onMultipleActivation && (
          <Button
            className="mr-md-2 mb-4 mb-md-0"
            onClick={onMultipleActivation}
            disabled={!grantedPermission}
          >
            <VscActivateBreakpoints className="mr-2" />
            {multipleActivateBtnLabel}
          </Button>
        )}
      </TooltipWrapper>
    );
  };

  const renderImportButton = () => {
    return (
      <div className="d-block col-xs-12 col-md-auto mb-2">
        <Button
          btnType="secondary"
          className="btn-block"
          onClick={onImport}
        >
          <FaUpload className="mr-2" />
          {t('common.import')}
        </Button>
      </div>
    );
  };

  const renderShowSelectButton = () => {
    return (
      <TooltipWrapper
        id="tooltip-select"
        overlay={toggleSelectBtnLabel || ''}
      >
        <Button
          btnType="primary"
          className="mr-md-2 mb-4 mb-md-0"
          onClick={() => {
            setShowSelectColumn(true);
          }}
        >
          <BiSelectMultiple className="mr-2" />
          {t('form.select')}
        </Button>
      </TooltipWrapper>
    );
  };

  const renderSelectionActionsButtons = () => {
    if (selectActionButtons?.length) {
      return selectActionButtons.map(button => (
        <Button
          key={`selected-act-btns-${button.btnType}-${button.label}`}
          btnType={button.btnType}
          className="mr-md-2 mb-4 mb-md-0"
          onClick={() => {
            button.onClick(getSelectedDataIds());
            setShowSelectColumn(false);
            toggleAllRowsSelected(false);
          }}
        >
          <span className="d-flex align-items-center">
            {button.icon}
            {button.label}
          </span>
        </Button>
      ));
    }
  };

  return (
    <>
      {!hideFilterBar && (
        <FiltersWrapper className="filterBar">
          <div className="d-flex">
            {onAdd && renderAddButton()}
            {onImport && hasImportPermission && renderImportButton()}
            {isSelectable && !showSelectColumn && !useCustomSelectBtn && renderShowSelectButton()}
            {isSelectable && showSelectColumn && renderSelectionActionsButtons()}
          </div>
          <ResponsiveFlexDiv>
            {showFilterWarning && hasActiveFilters && (
              <WarningIconWrapper className="d-none d-md-flex">
                <TooltipWrapper
                  id="filter-warning-tooltip"
                  overlay={t('table.filterWarning')}
                >
                  <FilterWarningIcon />
                </TooltipWrapper>
              </WarningIconWrapper>
            )}
            <TableFilters
              hideSearch={hideSearch}
              filters={filters}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              showFilterWarning={showFilterWarning}
              hasActiveFilters={hasActiveFilters}
              unprocessedLeaves={unprocessedLeaves}
            />
            {!hideSearch && (
              <GlobalFilter
                className="c-filter c-filter--max-width-190 col-xs-12"
                showFilters={showFilters}
                globalFilter={search || ''}
                setGlobalFilter={setSearch}
              />
            )}
          </ResponsiveFlexDiv>
        </FiltersWrapper>
      )}
      <TableComponent<T>
        loading={loading}
        error={error}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        getTableBodyProps={getTableBodyProps}
        toggleAllRowsExpanded={toggleAllRowsExpanded}
        rows={rows}
        visibleColumns={visibleColumns}
        prepareRow={prepareRow}
        className={className}
        entityName={entityName}
        docTab={docTab}
        expandAllRows={expandAllRows}
        isExpandable={isExpandable}
        isLeavesMobile={isLeavesMobile}
        setSortParam={setSortParam}
        setSortParamRef={setSortParamRef}
        useBackendSorting={useBackendSorting}
      />
      {pagination && totalCount > 0 && (
        <Pagination
          gotoPage={page => setOffset(page * limit)}
          canPreviousPage={offset > 0}
          previousPage={() => {
            const newOffset = offset - limit;
            if (newOffset) return setOffset(newOffset);
            return setOffset(0);
          }}
          nextPage={() => setOffset(offset + limit)}
          canNextPage={offset + limit < totalCount}
          pageCount={Math.ceil(totalCount / limit)}
          pageIndex={Math.floor(offset / limit)}
          pageOptions={pageOptions}
          pageSize={limit}
          total={totalCount}
          setPageSize={size => {
            if (size >= totalCount) setOffset(0);
            setLimit(size);
          }}
          disablePagination={disablePagination}
        />
      )}
    </>
  );
}

export const ResponsiveFlexDiv = styled.div`
  display: flex;
  align-items: start;
  justify-content: end;
  gap: 1rem;
  flex-grow: 1;

  @media (max-width: 991px) {
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    gap: 0;
  }
`;

export const FiltersWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  flex-wrap: nowrap !important;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;

  > div [class*='col-'] {
    margin-top: 8px;
  }

  @media (max-width: 768px) {
    display: block;
    margin: 0;
    margin-bottom: 0;

    .c-filter {
      padding: 0 !important;
    }

    > div [class*='col-'] {
      padding-right: 0px;
      padding-left: 0px;
    }

    > div [class*='col-'] {
      margin-bottom: 10px;
    }
  }

  @media (max-width: 576px) {
    flex-direction: column-reverse;
    align-items: stretch;
    justify-content: center;

    & > div {
      max-width: 100%;
    }
  }
`;

export const WarningIconWrapper = styled.div`
  margin-top: auto;
  margin-bottom: auto;
`;

export const FilterWarningIcon = styled(FaExclamationTriangle)`
  margin-top: 8px;
  color: #ffc107;
  font-size: 20px;
  display: flex;
  align-items: center;
`;

export default ReactTableWrapper;
